import React, { useState, useEffect, useRef, useCallback } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { DocAgent } from "./index";
import "./draggable-agent.css";
import { translate } from "../..";

interface DraggableAgentProps {
  visible: boolean;
  onHide: () => void;
}

interface Position {
  x: number;
  y: number;
}

const STORAGE_KEY = "assistant-panel-position";
const DEFAULT_POSITION: Position = { x: 100, y: 100 };

export function DraggableAgent({ visible, onHide }: DraggableAgentProps) {
  const [position, setPosition] = useState<Position>(DEFAULT_POSITION);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState<Position>({ x: 0, y: 0 });
  const dialogRef = useRef<Dialog>(null);

  // Load saved position from localStorage on mount
  useEffect(() => {
    const savedPosition = localStorage.getItem(STORAGE_KEY);
    if (savedPosition) {
      try {
        const parsed = JSON.parse(savedPosition) as Position;
        // Validate position is within screen bounds (will be properly bounded when dialog is rendered)
        setPosition(parsed);
      } catch (error) {
        console.error("Failed to parse saved assistant position:", error);
      }
    }
  }, []);

  // Save position to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(position));
  }, [position]);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (!dialogRef.current) return;

      const dialogElement = dialogRef.current.getElement();
      if (!dialogElement) return;

      // Calculate offset from mouse position to dialog's current state position
      // This ensures consistency between our position state and drag calculations
      setDragOffset({
        x: e.clientX - position.x,
        y: e.clientY - position.y,
      });
      setIsDragging(true);
      e.preventDefault();
    },
    [position]
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging) return;

      // Calculate new position based on actual mouse movement
      const newX = e.clientX - dragOffset.x;
      const newY = e.clientY - dragOffset.y;

      // Apply reasonable boundaries (allow some flexibility)
      const boundedX = Math.max(-50, Math.min(newX, window.innerWidth - 100));
      const boundedY = Math.max(-50, Math.min(newY, window.innerHeight - 100));

      setPosition({ x: boundedX, y: boundedY });
    },
    [isDragging, dragOffset]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Add global mouse event listeners when dragging
  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.body.style.userSelect = "none";

      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
        document.body.style.userSelect = "";
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // Handle window resize to keep dialog in bounds
  useEffect(() => {
    const handleResize = () => {
      if (!dialogRef.current) return;

      const dialogElement = dialogRef.current.getElement();
      if (!dialogElement) return;

      const rect = dialogElement.getBoundingClientRect();
      setPosition((prev) => ({
        x: Math.max(0, Math.min(prev.x, window.innerWidth - rect.width)),
        y: Math.max(0, Math.min(prev.y, window.innerHeight - rect.height)),
      }));
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Update dialog position when position state changes or when dialog becomes visible
  useEffect(() => {
    if (dialogRef.current && visible) {
      const dialogElement = dialogRef.current.getElement();
      if (dialogElement) {
        // Apply positioning styles immediately
        dialogElement.style.position = "fixed";
        dialogElement.style.left = `${position.x}px`;
        dialogElement.style.top = `${position.y}px`;
        dialogElement.style.margin = "0";
        dialogElement.style.transform = "none";
      }
    }
  }, [position, visible]);

  // Apply position immediately when dialog first becomes visible
  useEffect(() => {
    if (visible && dialogRef.current) {
      // Use a small delay to ensure the dialog has been rendered
      const timer = setTimeout(() => {
        const dialogElement = dialogRef.current?.getElement();
        if (dialogElement) {
          dialogElement.style.position = "fixed";
          dialogElement.style.left = `${position.x}px`;
          dialogElement.style.top = `${position.y}px`;
          dialogElement.style.margin = "0";
          dialogElement.style.transform = "none";
        }
      }, 0);

      return () => clearTimeout(timer);
    }
  }, [visible]);

  return (
    <Dialog
      ref={dialogRef}
      header={
        <div className={`draggable-header ${isDragging ? "dragging" : ""}`} onMouseDown={handleMouseDown}>
          <div className="header-content">
            <i className="pi pi-comment mr-2 header-icon"></i>
            <span>{translate("NT_ASSISTANT")}</span>
          </div>
          <Button icon="pi pi-times" className="p-button-rounded p-button-text header-close-btn" onClick={onHide} />
        </div>
      }
      visible={visible}
      onHide={onHide}
      className="draggable-assistant-dialog"
      modal={false}
      draggable={false}
      resizable={false}
      closable={false}
    >
      <div className="draggable-assistant-content">
        <DocAgent />
      </div>
    </Dialog>
  );
}
